import { useForm, Controller, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import FormDropdown from '../../components/form/FormDropdown';
import { useState, useEffect, useCallback } from 'react';
import { WebhookSubscription } from '../../models/webhookSubscriptions';
import { FormMultiSelect } from '../../components/form';

interface EditWebhookProps {
  onCancel: () => void;
  onSave?: (_subscription: WebhookSubscription) => void;
  subscription?: WebhookSubscription | null;
}

// allowed authentication types
export type AuthenticationType = 'None' | 'ApiKey' | 'OAuth' | string;

// additional header structure
export interface WebhookHeader {
  name: string;
  value: string;
}

// authentication settings
export interface AuthenticationSettings {
  authenticationType: AuthenticationType;
  tokenUrl?: string;
  grantType?: string;
  clientId?: string;
  clientSecret?: string;
  username?: string;
  scope?: string;
  additionalHeaders?: WebhookHeader[];
}

// webhookSubscription model
export interface WebhookSubscriptionForm {
  webhookSubscriptionId?: string;
  events: string[];
  url: string;
  additionalHeaders?: WebhookHeader[];
  authenticationSettings: AuthenticationSettings;
}

//form fields
export interface WebhookFormValues {
  url: string;
  events?: string[]; // optional
  additionalHeaders?: { name: string; value: string }[];
  authenticationType: AuthenticationType;
  apiKeyName?: string;
  apiKeyValue?: string;
  tokenUrl?: string;
  grantType?: string;
  clientId?: string;
  clientSecret?: string;
  username?: string;
  password?: string;
  scope?: string;
  oauthAdditionalHeaders?: { name: string; value: string }[]; // auth-specific
}

export const EditWebhook = ({ onCancel, onSave, subscription }: EditWebhookProps): JSX.Element => {
  const isEditMode = !!subscription;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // validation schema based on auth type
  const validationSchema = yup.object().shape({
    // Main webhook fields
    url: yup
      .string()
      .required('Webhook URL is required')
      .test('is-valid-url', 'URL must start with https:// and contain at least one dot', (value) => {
        if (!value) return false;
        return value.startsWith('https://') && value.includes('.');
      }),
    events: yup
      .array()
      .of(yup.string().required()) // each item must be a string
      .required('Registered events are required') // array itself is required
      .min(1, 'At least one event must be selected'),
    // Authentication type
    authenticationType: yup
      .mixed<AuthenticationType>()
      .oneOf(['None', 'ApiKey', 'OAuth'])
      .required('Authentication Type is required'),
    additionalHeaders: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Header name is required'),
          value: yup.string().required('Header value is required'),
        })
      )
      .optional(),
    // Fields for ApiKey
    apiKeyName: yup.string().when('authenticationType', {
      is: 'ApiKey',
      then: (schema) => schema.required('API Key Name is required'),
      otherwise: (schema) => schema.optional(),
    }),
    apiKeyValue: yup.string().when('authenticationType', {
      is: 'ApiKey',
      then: (schema) => schema.required('API Key Value is required'),
      otherwise: (schema) => schema.optional(),
    }),

    // Fields for OAuth
    tokenUrl: yup.string().when('authenticationType', {
      is: 'OAuth',
      then: (schema) =>
        schema
          .required('Token URL is required')
          .test('is-valid-url', 'URL must start with https:// and contain at least one dot', (value) => {
            if (!value) return false;
            return value.startsWith('https://') && value.includes('.');
          }),
      otherwise: (schema) => schema.optional(),
    }),
    grantType: yup.string().when('authenticationType', {
      is: 'OAuth',
      then: (schema) => schema.required('Grant Type is required'),
      otherwise: (schema) => schema.optional(),
    }),
    clientId: yup.string().when('authenticationType', {
      is: 'OAuth',
      then: (schema) => schema.required('Client ID is required'),
      otherwise: (schema) => schema.optional(),
    }),
    clientSecret: yup.string().when('authenticationType', {
      is: 'OAuth',
      then: (schema) => schema.required('Client Secret is required'),
      otherwise: (schema) => schema.optional(),
    }),
    username: yup.string().when(['authenticationType', 'grantType'], {
      is: (authType: string, grantType: string) => authType === 'OAuth' && grantType === 'password',
      then: (schema) => schema.required('Username is required'),
      otherwise: (schema) => schema.optional(),
    }),
    password: yup.string().when(['authenticationType', 'grantType'], {
      is: (authType: string, grantType: string) => authType === 'OAuth' && grantType === 'password',
      then: (schema) => schema.required('Password is required'),
      otherwise: (schema) => schema.optional(),
    }),
    scope: yup.string().when('authenticationType', {
      is: 'OAuth',
      then: (schema) => schema.required('Scope is required'),
      otherwise: (schema) => schema.optional(),
    }),
    oauthAdditionalHeaders: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required('Header name is required'),
          value: yup.string().required('Header value is required'),
        })
      )
      .when('authenticationType', {
        is: 'OAuth',
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const getFormDefaults = useCallback((): WebhookFormValues => ({
    url: subscription?.url ?? '',
    events: subscription?.events ?? [],
    additionalHeaders: subscription?.additionalHeaders ?? [],
    authenticationType: subscription?.authenticationSettings?.authenticationType ?? 'None',
    apiKeyName: subscription?.authenticationSettings?.apiKeyHeaderName ?? '',
    apiKeyValue: subscription?.authenticationSettings?.apiKeyHeaderValue ?? '',
    tokenUrl: subscription?.authenticationSettings?.tokenUrl ?? '',
    grantType: subscription?.authenticationSettings?.grantType ?? 'client_credentials',
    clientId: subscription?.authenticationSettings?.clientId ?? '',
    clientSecret: subscription?.authenticationSettings?.clientSecret ?? '',
    username: subscription?.authenticationSettings?.username ?? '',
    password: subscription?.authenticationSettings?.password ?? '',
    scope: subscription?.authenticationSettings?.scope ?? '',
    oauthAdditionalHeaders: subscription?.authenticationSettings?.additionalHeaders ?? [],
  }), [subscription]);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<WebhookFormValues>({
    resolver: yupResolver<WebhookFormValues>(validationSchema),
    defaultValues: getFormDefaults(),
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(getFormDefaults());
  }, [subscription, reset, getFormDefaults]);

  // Global headers
  const {
    fields: globalHeaders,
    append: appendGlobal,
    remove: removeGlobal,
  } = useFieldArray({
    control,
    name: 'additionalHeaders',
  });

  // OAuth-specific headers
  const {
    fields: oauthHeaders,
    append: appendOAuth,
    remove: removeOAuth,
  } = useFieldArray({
    control,
    name: 'oauthAdditionalHeaders', // new field in form state for oauth
  });

  const selectedAuthType = watch('authenticationType');
  const selectedGrantType = watch('grantType');

  const onSubmit = async (formData: WebhookFormValues) => {
    setIsSubmitting(true);

    try {
        const webhookData: WebhookSubscription = {
          webhookSubscriptionId: subscription?.webhookSubscriptionId,
          events: formData.events || [],
          url: formData.url,
          additionalHeaders: formData.additionalHeaders || [], // global headers
          authenticationSettings: {
            authenticationType: formData.authenticationType,
            tokenUrl: formData.authenticationType === 'OAuth' ? formData.tokenUrl || '' : '',
            grantType: formData.authenticationType === 'OAuth' ? formData.grantType || '' : '',
            clientId: formData.authenticationType === 'OAuth' ? formData.clientId || '' : '',
            clientSecret: formData.authenticationType === 'OAuth' ? formData.clientSecret || '' : '',
            username: formData.authenticationType === 'OAuth' ? formData.username || '' : '',
            password: formData.authenticationType === 'OAuth' ? formData.password || '' : '',
            scope: formData.authenticationType === 'OAuth' ? formData.scope || '' : '',
            additionalHeaders: formData.authenticationType === 'OAuth' ? formData.oauthAdditionalHeaders || [] : [], // auth-specific
            apiKeyHeaderName: formData.authenticationType === 'ApiKey' ? formData.apiKeyName || '' : '',
            apiKeyHeaderValue: formData.authenticationType === 'ApiKey' ? formData.apiKeyValue || '' : '',
          },
        };

      await onSave?.(webhookData);
    } catch (error) {
      console.error('Error saving subscription:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="surface-section p-4 surface-border border-round mb-3 w-full relative">
      <BlockUI blocked={isSubmitting}>
        {isSubmitting && (
          <div className="flex align-items-center justify-content-center" style={{ height: '100%' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled grid">
          {/* Auth Type Dropdown */}
          <div className="col-12 field mb-3 p-0">
            <FormInputText
              control={control}
              errors={errors}
              field="url"
              label="Webhook URL"
              placeholder="Webhook URL"
              required
            />
            <FormMultiSelect
              control={control}
              field="events"
              errors={errors}
              label="Registered Events"
              options={[
                { label: 'AUW Document Exception', value: 'AuwDocumentException' },
                { label: 'AUW Order Submitted', value: 'AuwOrderSubmitted' },
                { label: 'AUW Service Request Submitted', value: 'AuwServiceRequestSubmitted' },
                { label: 'AUW Service Response Received', value: 'AuwServiceResponseReceived' },
                { label: 'Flood Provider API Call Completed', value: 'FloodProviderApiCallCompleted' },
                { label: 'Flood Provider API Call Initiated', value: 'FloodProviderApiCallInitiated' },
                { label: 'Vox Provider API Call Completed', value: 'VoxProviderApiCallCompleted' },
                { label: 'Vox Provider API Call Initiated', value: 'VoxProviderApiCallInitiated' },
                { label: 'Vox Provider Data Refreshed', value: 'VoxProviderDataRefreshed' },
                { label: 'Vox Provider Disconnected', value: 'VoxProviderDisconnected' },
                { label: 'Vox Provider Passthrough', value: 'VoxProviderPassthrough' },
                { label: 'Waterfall Completed', value: 'WaterfallCompleted' },
              ]}
            />
            <div className="col-12 field mb-3 p-0">
              <label className="font-semibold mb-2 block">Additional Headers</label>
              {globalHeaders.map((field, index) => (
                <div key={field.id} className="flex gap-2 mb-2">
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`additionalHeaders.${index}.name`}
                    label="Header Name"
                    placeholder="Header Name"
                    required
                  />
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`additionalHeaders.${index}.value`}
                    label="Header Value"
                    placeholder="Header Value"
                    required
                  />
                  <button type="button" onClick={() => removeGlobal(index)}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => appendGlobal({ name: '', value: '' })}>
                + Add Header
              </button>
              {errors.additionalHeaders?.message && (
                <small className="p-error block mt-2">{errors.additionalHeaders.message}</small>
              )}
            </div>
            <label htmlFor="authenticationType" className="font-semibold mb-2 block">
              Authentication Type
            </label>
            <Controller
              name="authenticationType"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  options={[
                    { label: 'ApiKey', value: 'ApiKey' },
                    { label: 'None', value: 'None' },
                    { label: 'OAuth', value: 'OAuth' },
                  ]}
                  placeholder="Select Authentication Type"
                  className={errors.authenticationType ? 'p-invalid' : ''}
                  appendTo="self"
                />
              )}
            />
            {errors.authenticationType && <small className="p-error">{errors.authenticationType.message}</small>}
          </div>

          {/* Conditional Fields */}
          {selectedAuthType === 'ApiKey' && (
            <div className="flex gap-3">
              <FormInputText
                control={control}
                errors={errors}
                field="apiKeyName"
                label="API Key Name"
                placeholder="API Key Name"
                required
              />
              <FormPassword
                control={control}
                errors={errors}
                field="apiKeyValue"
                label="API Key Value"
                placeholder="API Key Value"
                required
              />
            </div>
          )}

          {selectedAuthType === 'OAuth' && (
            <div className="col-12 field mb-3 p-0">
              <FormInputText
                control={control}
                errors={errors}
                field="tokenUrl"
                label="Token URL"
                required
                placeholder="Token URL"
              />
              <FormDropdown
                control={control}
                errors={errors}
                field="grantType"
                label="Grant Type"
                placeholder="Select Grant Type"
                required
                options={[
                  { label: 'Client Credentials', value: 'client_credentials' },
                  { label: 'Password', value: 'password' },
                ].sort((a, b) => a.label.localeCompare(b.label))}
              />
              <FormInputText
                control={control}
                errors={errors}
                field="clientId"
                label="Client ID"
                placeholder="Client ID"
                required
              />
              <FormPassword
                control={control}
                errors={errors}
                field="clientSecret"
                label="Client Secret"
                placeholder="Client Secret"
                required
              />
              {selectedGrantType === 'password' && (
                <>
                  <FormInputText
                    control={control}
                    errors={errors}
                    field="username"
                    label="Username"
                    placeholder="Username"
                    required
                  />
                  <FormPassword
                    control={control}
                    errors={errors}
                    field="password"
                    label="Password"
                    placeholder="Password"
                    required
                  />
                </>
              )}
              <FormInputText control={control} errors={errors} field="scope" label="Scope" placeholder="Scope" required />
            </div>
          )}
          {selectedAuthType === 'OAuth' && (
            <div>
              <label className="font-semibold mb-2 block">OAuth Additional Headers</label>
              {oauthHeaders.map((field, index) => (
                <div key={field.id} className="flex gap-2">
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`oauthAdditionalHeaders.${index}.name`}
                    label="Name"
                    placeholder="Header Name"
                  />
                  <FormInputText
                    control={control}
                    errors={errors}
                    field={`oauthAdditionalHeaders.${index}.value`}
                    label="Value"
                    placeholder="Header Value"
                  />
                  <button type="button" onClick={() => removeOAuth(index)}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => appendOAuth({ name: '', value: '' })}>
                + Add Header
              </button>
            </div>
          )}

          <div className="col-12 flex flex-column sm:flex-row justify-content-end gap-2 pt-4">
            <Button type="button" label="Cancel" className="p-button-outlined w-full sm:w-auto" onClick={onCancel} />
            <Button type="submit" label={isEditMode ? 'Update' : 'Save'} className="p-button w-full sm:w-auto" />
          </div>
        </form>
      </BlockUI>
    </div>
  );
};
